/**
 * ActivityHeatmap
 * GitHub-style contribution calendar showing conversation activity.
 * Grid: 7 rows (Mon-Sun) × columns (one per week) covering the last `period` days.
 * 4 intensity levels, month labels, legend row, Ant Design Tooltip per cell.
 */

import { useMemo } from 'react';
import { activeLocale } from '@/utils/locale';
import { useLanguage } from '@/hooks/useLanguage';
import { Tooltip, Typography, Skeleton } from 'antd';
import type { TimeseriesPoint } from '../../types/insights';
import { useChartTheme } from '../../utils/chartTheme';

const { Text } = Typography;

// ─── Constants ────────────────────────────────────────────────────────────────

const CELL_SIZE = 16;
const CELL_GAP = 3;

// ─── Date helpers ─────────────────────────────────────────────────────────────

function toDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function formatDateLabel(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString(activeLocale(), { weekday: 'short', month: 'short', day: 'numeric' });
}

function getMonthAbbr(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString(activeLocale(), { month: 'short' });
}

// ─── Grid builder ─────────────────────────────────────────────────────────────

interface HeatmapColumn {
  weekStart: string;
  cells: Array<{ date: string; count: number; isCurrentMonth: boolean }>;
  showMonthLabel: boolean;
  monthLabel: string;
}

function buildGrid(countsMap: Record<string, number>, period: number): HeatmapColumn[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const startRaw = addDays(today, -period + 1);
  const dayOfWeek = startRaw.getDay();
  const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const gridStart = addDays(startRaw, -daysToMonday);

  const columns: HeatmapColumn[] = [];
  let cursor = new Date(gridStart);
  let lastMonthLabel = '';

  while (cursor <= today) {
    const weekStart = toDateStr(cursor);
    const cells = [];

    for (let dow = 0; dow < 7; dow++) {
      const cellDate = addDays(cursor, dow);
      const dateStr = toDateStr(cellDate);
      const count = countsMap[dateStr] ?? 0;
      cells.push({ date: dateStr, count, isCurrentMonth: cellDate <= today });
    }

    const monthLabel = getMonthAbbr(weekStart);
    const showMonthLabel = monthLabel !== lastMonthLabel;
    if (showMonthLabel) lastMonthLabel = monthLabel;

    columns.push({ weekStart, cells, showMonthLabel, monthLabel });
    cursor = addDays(cursor, 7);
  }

  return columns;
}

// ─── Props & component ────────────────────────────────────────────────────────

export interface ActivityHeatmapProps {
  agentTimeseries: TimeseriesPoint[];
  period: number;
  loading: boolean;
}

export function ActivityHeatmap({ agentTimeseries, period, loading }: ActivityHeatmapProps) {
  const { t } = useLanguage();
  const ct = useChartTheme();

  const intensityColors = useMemo(
    () => [ct.heatmapEmpty, ct.heatmapLow, ct.heatmapMed, ct.heatmapHigh],
    [ct],
  );

  function intensityColor(count: number): string {
    if (count === 0) return intensityColors[0];
    if (count <= 3) return intensityColors[1];
    if (count <= 10) return intensityColors[2];
    return intensityColors[3];
  }

  const countsMap = useMemo<Record<string, number>>(() => {
    const map: Record<string, number> = {};
    for (const pt of agentTimeseries) {
      if (!pt.date) continue;
      map[pt.date] = (map[pt.date] ?? 0) + pt.conversations;
    }
    return map;
  }, [agentTimeseries]);

  const columns = useMemo(() => buildGrid(countsMap, period), [countsMap, period]);

  const totalConversations = useMemo(
    () => Object.values(countsMap).reduce((sum, c) => sum + c, 0),
    [countsMap],
  );

  const DOW_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  return (
    <div>
      {loading ? (
        <Skeleton.Input active style={{ width: '100%', height: 140, borderRadius: 8 }} />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          {/* Centered grid */}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{ display: 'inline-flex', gap: 0 }}>
              {/* Day-of-week labels column */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: CELL_GAP, marginInlineEnd: CELL_GAP * 2 }}>
                <div style={{ height: 16 }} />
                {DOW_LABELS.map((label, i) => (
                  <div
                    key={i}
                    style={{
                      width: 14,
                      height: CELL_SIZE,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 10,
                      fontWeight: 500,
                      color: ct.mutedText,
                    }}
                  >
                    {i % 2 === 0 ? label : ''}
                  </div>
                ))}
              </div>

              {/* Week columns */}
              {columns.map(col => (
                <div
                  key={col.weekStart}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: CELL_GAP,
                    marginInlineEnd: CELL_GAP,
                  }}
                >
                  {/* Month label */}
                  <div
                    style={{
                      height: 16,
                      fontSize: 10,
                      fontWeight: 600,
                      color: ct.mutedText,
                      whiteSpace: 'nowrap',
                      overflow: 'visible',
                    }}
                  >
                    {col.showMonthLabel ? col.monthLabel : ''}
                  </div>

                  {/* Day cells */}
                  {col.cells.map(cell => (
                    <Tooltip
                      key={cell.date}
                      title={
                        cell.isCurrentMonth
                          ? `${formatDateLabel(cell.date)} — ${t('counts.conversations', { count: cell.count })}`
                          : undefined
                      }
                      mouseEnterDelay={0.1}
                    >
                      <div
                        style={{
                          width: CELL_SIZE,
                          height: CELL_SIZE,
                          borderRadius: 4,
                          background: cell.isCurrentMonth ? intensityColor(cell.count) : ct.heatmapOutside,
                          border: `1px solid ${ct.heatmapCellBorder}`,
                          cursor: cell.isCurrentMonth ? 'default' : 'not-allowed',
                          flexShrink: 0,
                          transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                        }}
                        onMouseEnter={(e) => {
                          if (cell.isCurrentMonth) {
                            e.currentTarget.style.transform = 'scale(1.3)';
                            e.currentTarget.style.zIndex = '1';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = '';
                          e.currentTarget.style.zIndex = '';
                          e.currentTarget.style.boxShadow = '';
                        }}
                      />
                    </Tooltip>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Legend */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, justifyContent: 'center' }}>
            <Text style={{ fontSize: 11, color: ct.mutedText }}>{t('common.less')}</Text>
            {intensityColors.map((color, i) => (
              <div
                key={i}
                style={{
                  width: CELL_SIZE,
                  height: CELL_SIZE,
                  borderRadius: 4,
                  background: color,
                  border: `1px solid ${ct.heatmapCellBorder}`,
                  flexShrink: 0,
                }}
              />
            ))}
            <Text style={{ fontSize: 11, color: ct.mutedText }}>{t('common.more')}</Text>
            <Text style={{ fontSize: 11, color: ct.mutedText, marginInlineStart: 8 }}>
              {t('insights.totalConversationsCount', { count: totalConversations.toLocaleString(activeLocale()) })}
            </Text>
          </div>
        </div>
      )}
    </div>
  );
}
